<template>
    <div class="min-h-screen bg-[#F7F7F8] text-slate-900 font-sans pb-20">

        <!-- Modal Voir Chantier -->
        <modal name="modal-view-chantier" height="auto" :adaptive="true" width="1100px" classes="rounded-2xl shadow-2xl overflow-hidden">
            <div class="bg-white p-2">
                <div class="overflow-y-auto max-h-[90vh] min-h-[80vh] rounded-xl bg-gray-50/50 p-8">
                    <div v-if="selectedChantier">
                        <div class="flex justify-between items-center mb-8">
                            <div class="flex items-center gap-3">
                                <div class="w-10 h-10 rounded-xl bg-orange-600 text-white flex items-center justify-center">
                                    <i class="fas fa-hard-hat text-sm"></i>
                                </div>
                                <h2 class="text-xl font-bold tracking-tight">Détails du chantier</h2>
                            </div>
                            <button @click="$modal.hide('modal-view-chantier')" class="w-9 h-9 rounded-lg hover:bg-slate-100 transition-all flex items-center justify-center text-slate-400 hover:text-red-500">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                        <chantier-view :chantier-id="selectedChantier.id" @chantier-ended="onChantierEnded" />
                    </div>
                </div>
            </div>
        </modal>

        <!-- Modal Clôture en masse -->
        <modal name="modal-cloturer-bulk" height="auto" :adaptive="true" width="520px" :click-to-close="!isCloturingBulk" classes="rounded-2xl shadow-2xl overflow-hidden">
            <div class="bg-white p-6">

                <!-- En-tête -->
                <div class="flex items-start justify-between mb-6">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-xl flex items-center justify-center" :class="bulkClotureDone ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'">
                            <i :class="bulkClotureDone ? 'fas fa-check' : 'fas fa-flag-checkered'" class="text-sm"></i>
                        </div>
                        <div>
                            <h2 class="text-base font-bold text-slate-800 leading-tight">
                                {{ bulkClotureDone ? 'Clôture terminée' : 'Clôturer les chantiers' }}
                            </h2>
                            <p class="text-xs text-slate-400 mt-0.5">
                                {{ bulkClotureItems.filter(i => i.done).length }} / {{ bulkClotureItems.length }} traité{{ bulkClotureItems.filter(i => i.done).length > 1 ? 's' : '' }}
                            </p>
                        </div>
                    </div>
                    <button v-if="!isCloturingBulk" @click="$modal.hide('modal-cloturer-bulk')" class="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors">
                        <i class="fas fa-times text-xs"></i>
                    </button>
                </div>

                <!-- Barre de progression globale -->
                <div class="mb-5">
                    <div class="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div
                            class="h-full rounded-full transition-all duration-500"
                            :class="bulkClotureDone ? 'bg-green-500' : 'bg-orange-500'"
                            :style="{ width: bulkClotureItems.length ? ((bulkClotureItems.filter(i => i.done || i.error).length / bulkClotureItems.length) * 100) + '%' : '0%' }"
                        ></div>
                    </div>
                </div>

                <!-- Liste des chantiers -->
                <div class="space-y-2 max-h-72 overflow-y-auto pr-1">
                    <div
                        v-for="item in bulkClotureItems"
                        :key="item.id"
                        class="flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-300"
                        :class="{
                            'border-slate-100 bg-slate-50': !item.loading && !item.done && !item.error,
                            'border-orange-100 bg-orange-50': item.loading,
                            'border-green-100 bg-green-50': item.done,
                            'border-red-100 bg-red-50': item.error,
                        }"
                    >
                        <!-- Icône état -->
                        <div class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300"
                            :class="{
                                'bg-slate-100': !item.loading && !item.done && !item.error,
                                'bg-orange-100': item.loading,
                                'bg-green-100': item.done,
                                'bg-red-100': item.error,
                            }"
                        >
                            <!-- Loader -->
                            <svg v-if="item.loading" class="animate-spin w-3.5 h-3.5 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <!-- Check -->
                            <i v-else-if="item.done" class="fas fa-check text-[10px] text-green-600"></i>
                            <!-- Erreur -->
                            <i v-else-if="item.error" class="fas fa-exclamation text-[10px] text-red-500"></i>
                            <!-- En attente -->
                            <i v-else class="fas fa-clock text-[10px] text-slate-300"></i>
                        </div>

                        <!-- Initiales + nom -->
                        <div class="flex items-center gap-2.5 flex-1 min-w-0">
                            <div class="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0 transition-all duration-300"
                                :class="{
                                    'bg-slate-200 text-slate-500': !item.loading && !item.done && !item.error,
                                    'bg-orange-200 text-orange-700': item.loading,
                                    'bg-green-200 text-green-700': item.done,
                                    'bg-red-200 text-red-600': item.error,
                                }"
                            >
                                {{ item.initiales }}
                            </div>
                            <div class="min-w-0">
                                <p class="text-sm font-semibold text-slate-700 truncate capitalize">{{ item.clientNom }}</p>
                                <p v-if="item.error" class="text-[10px] text-red-400 mt-0.5">Erreur lors de la clôture</p>
                                <p v-else-if="item.done" class="text-[10px] text-green-500 mt-0.5">Clôturé avec succès</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <div v-if="bulkClotureItems.some(i => i.error)" class="text-xs text-red-400 flex items-center gap-1.5">
                        <i class="fas fa-triangle-exclamation"></i>
                        {{ bulkClotureItems.filter(i => i.error).length }} échec{{ bulkClotureItems.filter(i => i.error).length > 1 ? 's' : '' }}
                    </div>
                    <div v-else class="text-xs text-slate-400"></div>

                    <button
                        v-if="bulkClotureDone || !isCloturingBulk"
                        @click="onBulkClotureFinished"
                        class="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                        :class="bulkClotureDone ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'"
                    >
                        {{ bulkClotureDone ? 'Fermer' : 'Annuler' }}
                    </button>
                    <button v-else disabled class="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-100 text-slate-400 cursor-not-allowed flex items-center gap-2">
                        <svg class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        En cours...
                    </button>
                </div>
            </div>
        </modal>

        <!-- Modal Nouveau Chantier -->
        <modal name="modal-new-chantier" width="90%" height="auto" :scrollable="true" :adaptive="true">
            <div class="bg-white p-2">
                <div class="overflow-y-auto max-h-[90vh] min-h-[80vh] rounded-xl bg-gray-50/50 p-8">
                    <div>
                        <div class="flex justify-between items-center mb-8">
                            <div class="flex items-center gap-3">
                                <div class="w-10 h-10 rounded-xl bg-orange-600 text-white flex items-center justify-center">
                                    <i class="fas fa-plus text-sm"></i>
                                </div>
                                <h2 class="text-xl font-bold tracking-tight">Nouveau chantier</h2>
                            </div>
                            <button @click="$modal.hide('modal-new-chantier')" class="w-9 h-9 rounded-lg hover:bg-slate-100 transition-all flex items-center justify-center text-slate-400 hover:text-red-500">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                        <chantier-create @chantier-created="onChantierCreated" />
                    </div>
                </div>
            </div>
        </modal>

        <app-template>
            <div class="max-w-[1400px] mx-auto px-6 py-8">

                <!-- En-tête -->
                <header class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 class="text-2xl font-bold text-slate-900 tracking-tight">Chantiers</h1>
                        <p class="text-sm text-slate-500 mt-0.5">{{ pagination.total || 0 }} chantier{{ (pagination.total || 0) > 1 ? 's' : '' }} au total</p>
                    </div>
                    <button @click="$modal.show('modal-new-chantier')" class="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-sm">
                        <i class="fas fa-plus text-xs"></i>
                        Nouveau chantier
                    </button>
                </header>

                <!-- Filtres -->
                <div class="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
                    <div class="flex items-center justify-between mb-4">
                        <span class="text-sm font-semibold text-slate-700 flex items-center gap-2">
                            <i class="fas fa-filter text-slate-400"></i>
                            Filtres
                        </span>
                        <button @click="resetFilters" class="text-xs text-slate-400 hover:text-orange-600 font-medium transition-colors flex items-center gap-1">
                            <i class="fas fa-rotate-left text-[10px]"></i>
                            Réinitialiser
                        </button>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                        <div class="relative">
                            <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs"></i>
                            <input v-model="filters.search" @input="onSearchInput" type="text" placeholder="Client, email, téléphone..." class="pl-9 pr-3 w-full rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm py-2.5 placeholder:text-slate-300" />
                        </div>

                        <select v-model="filters.statut_id" @change="onFilterChange" class="px-3 w-full rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm py-2.5 text-slate-700">
                            <option value="">Tous les statuts</option>
                            <option v-for="statut in statuts" :key="statut.id" :value="statut.id">{{ statut.libelle }}</option>
                        </select>

                        <div class="relative">
                            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs pointer-events-none">Début</span>
                            <input v-model="filters.date_debut" @change="onFilterChange" type="date" class="pl-12 pr-3 w-full rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm py-2.5 text-slate-700" />
                        </div>

                        <div class="relative">
                            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs pointer-events-none">Fin</span>
                            <input v-model="filters.date_fin" @change="onFilterChange" type="date" class="pl-9 pr-3 w-full rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm py-2.5 text-slate-700" />
                        </div>

                        <label class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-slate-200 bg-white cursor-pointer select-none hover:border-orange-300 transition-colors">
                            <input v-model="filters.show_closed" @change="onFilterChange" type="checkbox" class="rounded border-slate-300 text-orange-600 focus:ring-orange-500 focus:ring-offset-0" />
                            <span class="text-sm text-slate-600 font-medium">Afficher terminés</span>
                        </label>
                    </div>
                </div>

                <!-- Barre d'actions sélection -->
                <transition name="slide-action">
                    <div v-if="selectedIds.length > 0" class="bg-orange-600 text-white rounded-xl px-5 py-3 mb-4 flex items-center justify-between shadow-lg shadow-orange-200">
                        <span class="text-sm font-semibold">
                            {{ selectedIds.length }} chantier{{ selectedIds.length > 1 ? 's' : '' }} sélectionné{{ selectedIds.length > 1 ? 's' : '' }}
                        </span>
                        <div class="flex items-center gap-2">
                            <button @click="openBulkClotureModal" class="inline-flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all border border-white/20 hover:border-white/40">
                                <i class="fas fa-flag-checkered text-[10px]"></i>
                                Clôturer
                            </button>
                            <button @click="clearSelection" class="text-orange-200 hover:text-white text-xs font-medium flex items-center gap-1.5 ml-1 px-2 py-1.5 rounded-lg hover:bg-white/10 transition-all">
                                <i class="fas fa-times text-[10px]"></i>
                                Désélectionner
                            </button>
                        </div>
                    </div>
                </transition>

                <!-- Tableau -->
                <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden">

                    <!-- Skeleton -->
                    <div v-if="loadingGetChantiers">
                        <div class="border-b border-slate-100 px-5 py-3 flex items-center gap-4">
                            <div class="w-4 h-4 bg-slate-100 rounded animate-pulse"></div>
                            <div class="w-24 h-3 bg-slate-100 rounded animate-pulse"></div>
                            <div class="w-32 h-3 bg-slate-100 rounded animate-pulse ml-4"></div>
                            <div class="w-20 h-3 bg-slate-100 rounded animate-pulse ml-auto"></div>
                        </div>
                        <div v-for="i in 8" :key="'skel' + i" class="border-b border-slate-50 px-5 py-4 flex items-center gap-4 animate-pulse">
                            <div class="w-4 h-4 bg-slate-100 rounded"></div>
                            <div class="w-10 h-10 bg-slate-100 rounded-xl flex-shrink-0"></div>
                            <div class="space-y-1.5 flex-1">
                                <div class="w-36 h-3.5 bg-slate-100 rounded"></div>
                                <div class="w-24 h-2.5 bg-slate-50 rounded"></div>
                            </div>
                            <div class="w-20 h-6 bg-slate-50 rounded-full"></div>
                            <div class="w-28 h-3 bg-slate-50 rounded hidden md:block"></div>
                            <div class="w-32 h-3 bg-slate-50 rounded hidden lg:block"></div>
                            <div class="w-24 h-2.5 bg-slate-50 rounded hidden xl:block"></div>
                        </div>
                    </div>

                    <!-- Vide -->
                    <div v-else-if="chantiers.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
                        <div class="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mb-4 text-slate-200">
                            <i class="fas fa-mountain-city text-2xl"></i>
                        </div>
                        <h3 class="text-base font-semibold text-slate-700">Aucun chantier trouvé</h3>
                        <p class="text-sm text-slate-400 mt-1">Modifiez vos filtres ou créez un nouveau chantier.</p>
                    </div>

                    <!-- Table -->
                    <table v-else class="w-full text-sm">
                        <thead>
                            <tr class="border-b border-slate-100 bg-slate-50/60">
                                <th class="px-5 py-3 text-left w-10">
                                    <input
                                        type="checkbox"
                                        :checked="isAllSelected"
                                        :indeterminate.prop="isSomeSelected"
                                        @change="toggleSelectAll"
                                        class="rounded border-slate-300 text-orange-600 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer"
                                    />
                                </th>
                                <th class="px-5 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider">Client</th>
                                <th class="px-5 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider">Statut</th>
                                <th class="px-5 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider hidden md:table-cell">Ville</th>
                                <th class="px-5 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider hidden lg:table-cell">Adresse</th>
                                <th class="px-5 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider hidden xl:table-cell">Progression</th>
                                <th class="px-5 py-3 text-right w-12"></th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-slate-50">
                            <tr
                                v-for="chantier in chantiers"
                                :key="chantier.id"
                                class="transition-colors group"
                                :class="{
                                    'hover:bg-orange-50/40': !chantier.terminer,
                                    'bg-slate-50/50 opacity-60': chantier.terminer,
                                    'bg-orange-50/60': selectedIds.includes(chantier.id),
                                }"
                            >
                                <!-- Checkbox -->
                                <td class="px-5 py-3.5" @click.stop>
                                    <div class="flex items-center gap-4">
                                        <input
                                            v-if="!chantier.terminer"
                                            type="checkbox"
                                            :value="chantier.id"
                                            v-model="selectedIds"
                                            class="rounded border-slate-300 text-orange-600 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer"
                                        />
                                        <span v-else class="w-4 h-4 flex items-center justify-center" title="Chantier déjà terminé">
                                            <i class="fas fa-lock text-slate-300 text-[10px]"></i>
                                        </span>
                                        <button v-if="!chantier.terminer" @click.stop="deleteChantier(chantier)" class="w-6 h-6 rounded hover:bg-red-100 flex items-center justify-center transition-colors" :title="'Supprimer ' + (chantier.client ? chantier.client.prenom + ' ' + chantier.client.nom : 'le chantier')">
                                            <i class="fas fa-trash text-red-500"></i>
                                        </button>
                                    </div>
                                </td>

                                <!-- Client -->
                                <td class="px-5 py-3.5 cursor-pointer" @click="openChantier(chantier)">
                                    <div class="flex items-center gap-3">
                                        <div class="w-9 h-9 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                            {{ getClientInitiales(chantier) }}
                                        </div>
                                        <div>
                                            <p class="font-semibold text-slate-800 capitalize leading-tight">
                                                {{ chantier.client ? chantier.client.prenom : '' }} {{ chantier.client ? chantier.client.nom : '' }}
                                            </p>
                                            <p class="text-xs text-slate-400 mt-0.5" v-if="chantier.client && chantier.client.email">{{ chantier.client.email }}</p>
                                        </div>
                                    </div>
                                </td>

                                <!-- Statut -->
                                <td class="px-5 py-3.5 cursor-pointer" @click="openChantier(chantier)">
                                    <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-orange-100 text-orange-700">
                                        <i :class="['fas text-[9px]', chantier.statut && chantier.statut.icone ? chantier.statut.icone : 'fa-circle']"></i>
                                        {{ chantier.statut ? chantier.statut.libelle : 'Sans statut' }}
                                    </span>
                                </td>

                                <!-- Ville -->
                                <td class="px-5 py-3.5 text-slate-600 hidden md:table-cell cursor-pointer" @click="openChantier(chantier)">
                                    <span class="flex items-center gap-1.5">
                                        <i class="fas fa-location-dot text-slate-300 text-xs"></i>
                                        {{ chantier.adresse ? chantier.adresse.ville : '—' }}
                                    </span>
                                </td>

                                <!-- Adresse -->
                                <td class="px-5 py-3.5 text-slate-400 text-xs hidden lg:table-cell cursor-pointer" @click="openChantier(chantier)">
                                    {{ chantier.adresse ? chantier.adresse.adresse1 : '—' }}
                                </td>

                                <!-- Progression -->
                                <td class="px-5 py-3.5 hidden xl:table-cell cursor-pointer" @click="openChantier(chantier)">
                                    <div class="flex items-center gap-2.5 min-w-[120px]">
                                        <div class="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div class="h-full bg-orange-500 rounded-full transition-all duration-700" :style="{ width: getProgressionWidth(chantier) }"></div>
                                        </div>
                                        <span class="text-xs text-slate-400 font-medium tabular-nums whitespace-nowrap">
                                            {{ nbInterventionEndedInChantier(chantier) }}/{{ getInterventionsCount(chantier) }}
                                        </span>
                                    </div>
                                </td>

                                <!-- Action -->
                                <td class="px-5 py-3.5 text-right">
                                    <button @click="openChantier(chantier)" class="w-8 h-8 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-orange-600 hover:border-orange-300 transition-all opacity-0 group-hover:opacity-100 flex items-center justify-center ml-auto">
                                        <i class="fas fa-arrow-right text-xs"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="pagination.last_page > 1" class="flex items-center justify-between mt-5">
                    <span class="text-sm text-slate-400">
                        Page {{ pagination.current_page }} sur {{ pagination.last_page }}
                    </span>

                    <div class="flex items-center gap-1.5">
                        <button :disabled="pagination.current_page <= 1" @click="changePage(pagination.current_page - 1)" class="w-9 h-9 rounded-lg bg-white border border-slate-200 text-slate-600 font-medium text-sm hover:border-orange-300 hover:text-orange-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center">
                            <i class="fas fa-chevron-left text-xs"></i>
                        </button>

                        <button v-for="page in visiblePages" :key="'page-' + page" @click="changePage(page)" class="w-9 h-9 rounded-lg border font-medium text-sm transition-all flex items-center justify-center" :class="page === pagination.current_page ? 'bg-orange-600 border-orange-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-orange-300 hover:text-orange-600'">
                            {{ page }}
                        </button>

                        <button :disabled="pagination.current_page >= pagination.last_page" @click="changePage(pagination.current_page + 1)" class="w-9 h-9 rounded-lg bg-white border border-slate-200 text-slate-600 font-medium text-sm hover:border-orange-300 hover:text-orange-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center">
                            <i class="fas fa-chevron-right text-xs"></i>
                        </button>
                    </div>
                </div>

            </div>
        </app-template>
    </div>
</template>

<script>
import ChantierView from './Components/ChantierView.vue';
import ChantierForm from './Components/ChantierForm.vue';
import ChantierCreate from './Components/ChantierCreate.vue';

export default {
    components: {
        ChantierView,
        ChantierForm,
        ChantierCreate,
    },
    data() {
        return {
            chantiers: [],
            statuts: [],
            selectedChantier: null,
            selectedIds: [],
            isCloturingBulk: false,
            bulkClotureDone: false,
            bulkClotureItems: [],
            loadingGetChantiers: false,
            loadingCreateChantier: false,
            newChantier: {
                client_id: null,
                adresse_id: null,
            },
            filters: {
                search: '',
                statut_id: '',
                date_debut: '',
                date_fin: '',
                show_closed: false,
            },
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 50,
                total: 0,
            },
        };
    },
    computed: {
        visiblePages() {
            const current = this.pagination.current_page || 1;
            const last = this.pagination.last_page || 1;
            const delta = 2;
            const pages = [];
            for (let i = Math.max(1, current - delta); i <= Math.min(last, current + delta); i++) {
                pages.push(i);
            }
            return pages;
        },
        selectableChantiers() {
            return this.chantiers.filter(c => !c.terminer);
        },
        isAllSelected() {
            return this.selectableChantiers.length > 0 && this.selectableChantiers.every(c => this.selectedIds.includes(c.id));
        },
        isSomeSelected() {
            return this.selectedIds.length > 0 && !this.isAllSelected;
        },
    },
    created() {
        this.debouncedSearch = _.debounce(() => {
            this.getChantiers(1);
        }, 400);
    },
    methods: {
        async getChantiers(page = 1) {
            this.loadingGetChantiers = true;
            try {
                const payload = {
                    page,
                    search: this.filters.search,
                    statut_id: this.filters.statut_id,
                    date_debut: this.filters.date_debut,
                    date_fin: this.filters.date_fin,
                    show_closed: this.filters.show_closed ? 1 : 0,
                };
                const response = await this.$request.post('/chantiers', payload);
                const result = response;
                this.chantiers = result.data || [];
                this.pagination = {
                    current_page: result.current_page || 1,
                    last_page: result.last_page || 1,
                    per_page: result.per_page || 50,
                    total: result.total || 0,
                };
                this.extractStatutsFromChantiers();
                // Nettoie la sélection des IDs qui ne sont plus dans la page
                const pageIds = this.chantiers.map(c => c.id);
                this.selectedIds = this.selectedIds.filter(id => pageIds.includes(id));
            } catch (error) {
                console.error('Erreur API:', error);
                this.$toast.error("Une erreur est survenue lors du chargement des chantiers.");
            } finally {
                this.loadingGetChantiers = false;
            }
        },

        extractStatutsFromChantiers() {
            const map = {};
            this.chantiers.forEach(chantier => {
                if (chantier.statut && chantier.statut.id) {
                    map[chantier.statut.id] = chantier.statut;
                }
            });
            const merged = [...this.statuts];
            Object.values(map).forEach(statut => {
                if (!merged.find(item => String(item.id) === String(statut.id))) {
                    merged.push(statut);
                }
            });
            this.statuts = merged.sort((a, b) => a.libelle.localeCompare(b.libelle));
        },

        onSearchInput() {
            this.debouncedSearch();
        },

        onFilterChange() {
            this.getChantiers(1);
        },

        resetFilters() {
            this.filters = {
                search: '',
                statut_id: '',
                date_debut: '',
                date_fin: '',
                show_closed: false,
            };
            this.getChantiers(1);
        },

        async createChantier() {
            if (!this.newChantier.client_id || !this.newChantier.adresse_id) {
                this.$toast.error("Veuillez sélectionner un client et une adresse.");
                return;
            }
            try {
                this.loadingCreateChantier = true;
                const response = await this.$request.post('/chantiers/create', this.newChantier);
                const chantier = response.data || response;
                this.$modal.hide('modal-new-chantier');
                this.newChantier = { client_id: null, adresse_id: null };
                await this.getChantiers(this.pagination.current_page || 1);
                this.selectedChantier = chantier;
                this.$modal.show('modal-view-chantier');
            } catch (error) {
                console.error('Erreur lors de la création du chantier:', error);
                this.$toast.error("Une erreur est survenue lors de la création du chantier.");
            } finally {
                this.loadingCreateChantier = false;
            }
        },

        onChantierCreated() {
            this.getChantiers(1);
            this.$modal.hide('modal-new-chantier');
        },

        onChantierEnded() {
            this.getChantiers(this.pagination.current_page || 1);
            this.$modal.hide('modal-view-chantier');
        },

        openChantier(chantier) {
            this.selectedChantier = chantier;
            this.$modal.show('modal-view-chantier');
        },

        changePage(page) {
            if (page < 1 || page > this.pagination.last_page) return;
            this.getChantiers(page);
        },

        toggleSelectAll() {
            if (this.isAllSelected) {
                this.clearSelection();
            } else {
                this.selectedIds = this.selectableChantiers.map(c => c.id);
            }
        },

        openBulkClotureModal() {
            const chantiersSelectionnes = this.chantiers.filter(c => this.selectedIds.includes(c.id));

            this.bulkClotureItems = chantiersSelectionnes.map(c => ({
                id: c.id,
                clientNom: [c.client?.prenom, c.client?.nom].filter(Boolean).join(' ') || 'Inconnu',
                initiales: this.getClientInitiales(c),
                loading: false,
                done: false,
                error: false,
            }));

            this.bulkClotureDone = false;
            this.isCloturingBulk = false;
            this.$modal.show('modal-cloturer-bulk');

            // Lancement automatique après un court délai pour laisser la modal s'ouvrir
            this.$nextTick(() => {
                setTimeout(() => this.cloturerBulk(), 300);
            });
        },

        async cloturerBulk() {
            this.isCloturingBulk = true;

            for (const item of this.bulkClotureItems) {
                item.loading = true;

                try {
                    await this.$request.post('/chantiers/cloturer', { chantier_id: item.id });
                    item.done = true;
                } catch (e) {
                    item.error = true;
                    console.error(`Erreur clôture chantier ${item.id}:`, e);
                } finally {
                    item.loading = false;
                }
            }

            this.isCloturingBulk = false;
            this.bulkClotureDone = true;
        },

        onBulkClotureFinished() {
            this.$modal.hide('modal-cloturer-bulk');
            this.clearSelection();
            this.getChantiers(this.pagination.current_page || 1);
        },

        clearSelection() {
            this.selectedIds = [];
        },

        nbInterventionEndedInChantier(chantier) {
            if (!chantier.interventions || !Array.isArray(chantier.interventions)) return 0;
            return chantier.interventions.filter(i => i.statut && !['a_planifier', 'en_cours', 'planifie'].includes(i.statut.code)).length;
        },

        getInterventionsCount(chantier) {
            if (!chantier.interventions || !Array.isArray(chantier.interventions)) return 0;
            return chantier.interventions.length;
        },

        getProgressionWidth(chantier) {
            const total = this.getInterventionsCount(chantier);
            if (!total) return '0%';
            return ((this.nbInterventionEndedInChantier(chantier) / total) * 100) + '%';
        },

        getClientInitiales(chantier) {
            const prenom = chantier?.client?.prenom?.charAt(0) || '';
            const nom = chantier?.client?.nom?.charAt(0) || '';
            return (prenom + nom).toUpperCase() || '--';
        },
        
        async deleteChantier(chantier) {
            if (confirm(`Êtes-vous sûr de vouloir supprimer le chantier de ${chantier.client ? chantier.client.prenom + ' ' + chantier.client.nom : 'ce chantier'} ?`)) {
                let response = await this.$request.post('/chantiers/delete', { chantier_id: chantier.id });
                if (response == 1) {
                    this.$toast.success("Chantier supprimé avec succès.");
                    this.getChantiers(this.pagination.current_page || 1);
                } else {
                    this.$toast.error("Une erreur est survenue lors de la suppression du chantier.");
                }
            }
        }
    },
    mounted() {
        this.getChantiers();
        this.$eventHub.$on('refresh-intervention-table', () => {
            this.getChantiers();
        });
    },
};
</script>

<style scoped>
::-webkit-scrollbar { width: 5px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: #CBD5E1; }

.slide-action-enter-active,
.slide-action-leave-active {
    transition: all 0.25s ease;
}
.slide-action-enter-from,
.slide-action-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

tbody tr {
    transition: background-color 0.15s ease;
}
</style>